<template>
  <UmisisModal
    ref="taskDetailsModal"
    size="lg"
    title="Edit Task Details"
    description="Update the task title and description."
    ok-title="Save"
    cancel-title="Cancel"
    :ok-disabled="isSubmitDisabled"
    @ok="submitTaskDetails"
    @show="onModalShow"
    @hide="onModalHide"
  >
    <div class="flex flex-col gap-4">
      <Field>
        <FieldLabel for="task-title-input">
          Title
        </FieldLabel>
        <Input
          id="task-title-input"
          v-model="draftTitle"
          type="text"
          maxlength="255"
          placeholder="Enter task title"
          :disabled="isSaving"
          :is-error="Boolean(titleError)"
        />
        <FieldError v-if="titleError">
          {{ titleError }}
        </FieldError>
      </Field>

      <Field>
        <div class="flex items-center justify-between gap-3">
          <FieldLabel>
            Description
          </FieldLabel>
          <span class="text-xs text-muted-foreground">
            {{ editorStatusLabel }}
          </span>
        </div>

        <UmisisTiptapToolbar
          :actions="toolbarActions"
          :is-action-disabled="isToolbarActionDisabled"
          :is-action-active="isToolbarActionActive"
          @action="runToolbarAction"
        />

        <div class="tiptap-editor h-[50vh] min-h-64 max-h-96 overflow-y-auto rounded-md border border-input bg-transparent">
          <EditorContent :editor="editor" />
        </div>

        <FieldError v-if="descriptionError">
          {{ descriptionError }}
        </FieldError>
        <FieldDescription v-else>
          Capture context, progress notes, or completion details with rich text formatting.
        </FieldDescription>
      </Field>
    </div>
  </UmisisModal>
</template>

<script setup>
import Image from '@tiptap/extension-image'
import StarterKit from '@tiptap/starter-kit'
import { EditorContent, useEditor } from '@tiptap/vue-3'
import { computed, onBeforeUnmount, ref } from 'vue'
import { toast } from 'vue-sonner'
import { Field, FieldDescription, FieldError, FieldLabel } from '../../../../../../../resources/js/components/ui/field/index.js'
import { Input } from '../../../../../../../resources/js/components/ui/input/index.js'
import UmisisModal from '../../../../../../../resources/js/components/umisis/generic/UmisisModal.vue'
import UmisisTiptapToolbar from '../../../../../../../resources/js/components/umisis/generic/UmisisTiptapToolbar.vue'
import { useModalControl } from '../../../../../../../resources/js/composables/modal/useUmisisModal.js'
import { useTasksStore } from '../../stores/tasksStore.js'
import { useTiptapToolbarActions } from '../../composables/useTiptapToolbarActions.js'
import {
  createEmptyTaskRichTextDocument,
  parseTaskRichTextDescription,
  serializeTaskRichTextDocument,
  stringifyTaskRichTextDocument,
} from '../../utils/taskRichText.js'

const props = defineProps({
  task: {
    type: Object,
    required: true,
  },
  context: {
    type: String,
    default: 'task-show',
  },
})

const draftTitle = ref('')
const persistedTitleSnapshot = ref('')
const persistedDescriptionSnapshot = ref(stringifyTaskRichTextDocument(createEmptyTaskRichTextDocument()))
const editorContentSnapshot = ref(stringifyTaskRichTextDocument(createEmptyTaskRichTextDocument()))
const validationErrors = ref({})
const isSaving = ref(false)

const { componentRef: taskDetailsModal, openModal: openDialog, closeModal: closeDialog } = useModalControl()
const tasksStore = useTasksStore(props.context)
const normalizedTaskId = computed(() => Number(props.task?.id))
const titleError = computed(() => {
  const error = validationErrors.value?.title

  return Array.isArray(error) ? error[0] : (typeof error === 'string' ? error : '')
})
const descriptionError = computed(() => {
  const error = validationErrors.value?.description

  return Array.isArray(error) ? error[0] : (typeof error === 'string' ? error : '')
})
const hasUnsavedChanges = computed(() => {
  return draftTitle.value.trim() !== persistedTitleSnapshot.value
    || editorContentSnapshot.value !== persistedDescriptionSnapshot.value
})
const editorStatusLabel = computed(() => {
  if (isSaving.value) {
    return 'Saving changes...'
  }

  if (hasUnsavedChanges.value) {
    return 'Unsaved changes.'
  }

  return 'No changes to save.'
})
const isSubmitDisabled = computed(() => {
  return isSaving.value
    || !Number.isInteger(normalizedTaskId.value)
    || normalizedTaskId.value <= 0
    || draftTitle.value.trim() === ''
})

const editor = useEditor({
  extensions: [StarterKit, Image],
  content: createEmptyTaskRichTextDocument(),
  editorProps: {
    attributes: {
      class: 'min-h-full w-full px-3 py-2 text-sm focus:outline-none',
    },
  },
  onUpdate: ({ editor: currentEditor }) => {
    editorContentSnapshot.value = stringifyTaskRichTextDocument(currentEditor.getJSON())
  },
})

const toolbarActions = useTiptapToolbarActions()

const clearValidationErrors = () => {
  validationErrors.value = {}
}

const syncFormWithTask = () => {
  const normalizedTitle = typeof props.task?.title === 'string' ? props.task.title : ''
  const normalizedDescription = parseTaskRichTextDescription(props.task?.description ?? '')
  const normalizedDescriptionSnapshot = stringifyTaskRichTextDocument(normalizedDescription)

  draftTitle.value = normalizedTitle
  persistedTitleSnapshot.value = normalizedTitle.trim()
  persistedDescriptionSnapshot.value = normalizedDescriptionSnapshot
  editorContentSnapshot.value = normalizedDescriptionSnapshot
  editor.value?.commands.setContent(normalizedDescription, false)
}

const openModal = () => {
  openDialog()
}

const closeModal = () => {
  closeDialog()
}

const onModalShow = () => {
  clearValidationErrors()
  syncFormWithTask()
}

const onModalHide = () => {
  clearValidationErrors()
}

const runToolbarAction = (action) => {
  if (!editor.value || isSaving.value || typeof action.execute !== 'function') {
    return
  }

  action.execute(editor.value.chain().focus()).run()
}

const isToolbarActionDisabled = (action) => {
  if (!editor.value || isSaving.value) {
    return true
  }

  if (typeof action.execute !== 'function') {
    return false
  }

  return !action.execute(editor.value.can().chain().focus()).run()
}

const isToolbarActionActive = (action) => {
  if (!editor.value || typeof action.isActive !== 'function') {
    return false
  }

  return action.isActive(editor.value)
}

const submitTaskDetails = async (event) => {
  event?.preventDefault?.()
  clearValidationErrors()

  if (!Number.isInteger(normalizedTaskId.value) || normalizedTaskId.value <= 0) {
    return
  }

  const nextTitle = draftTitle.value.trim()

  if (nextTitle === '') {
    validationErrors.value = {
      title: ['Title is required.'],
    }
    return
  }

  const nextDescriptionDocument = editor.value?.getJSON() ?? createEmptyTaskRichTextDocument()
  const nextDescriptionSnapshot = stringifyTaskRichTextDocument(nextDescriptionDocument)

  if (
    nextTitle === persistedTitleSnapshot.value
    && nextDescriptionSnapshot === persistedDescriptionSnapshot.value
  ) {
    closeDialog()
    return
  }

  isSaving.value = true

  try {
    await tasksStore.itemPatch(normalizedTaskId.value, {
      title: nextTitle,
      description: serializeTaskRichTextDocument(nextDescriptionDocument),
    })
    toast.success('Task details updated.')
    closeDialog()
  } catch (error) {
    validationErrors.value = error?.response?.data?.errors ?? {}

    if (!error?.response?.data?.errors) {
      const errorMessage = error?.response?.data?.message ?? 'Unable to update task details.'

      toast.error(errorMessage)
    }
  } finally {
    isSaving.value = false
  }
}

onBeforeUnmount(() => {
  editor.value?.destroy()
})

defineExpose({
  openModal,
  closeModal,
})
</script>

<style scoped>
.tiptap-editor :deep(.ProseMirror) {
  min-height: 100%;
  white-space: pre-wrap;
}

.tiptap-editor :deep(.ProseMirror ul),
.tiptap-editor :deep(.ProseMirror ol) {
  padding-left: 1.5rem;
}

.tiptap-editor :deep(.ProseMirror ul) {
  list-style-type: disc;
}

.tiptap-editor :deep(.ProseMirror ol) {
  list-style-type: decimal;
}

.tiptap-editor :deep(.ProseMirror h2) {
  font-size: 1.25rem;
  font-weight: 600;
}

.tiptap-editor :deep(.ProseMirror img) {
  max-width: 100%;
  height: auto;
}
</style>
